import React from "react";
import { Loader2, Briefcase, MapPin, Calendar } from "lucide-react";
import { useCandidateExperiences } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { ExperienceDetail } from "@/interface/profile";

export function ExperienceTab({
  profileId,
  experiences = [],
}: {
  profileId?: number | string;
  experiences?: ExperienceDetail[];
}) {
  const {
    data: expQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateExperiences(profileId, { page: 1, page_size: 10 });

  const queryExp = expQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const expList: ExperienceDetail[] =
    queryExp && queryExp.length > 0
      ? queryExp
      : Array.isArray(experiences)
        ? experiences
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Work Experience
      </h2>

      {isLoading && expList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {expList.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No work experience records listed.
            </p>
          ) : (
            <div className="space-y-4">
              {expList.map((exp: ExperienceDetail, i: number) => {
                const duration =
                  exp.start_date || exp.end_date || exp.currently_working
                    ? `${exp.start_date || ""} - ${
                        exp.currently_working ? "Present" : exp.end_date || "Present"
                      }`
                    : exp.startDate || exp.endDate || "";

                const skills = (exp.skills_used || exp.technologies || []).map(
                  (skill) =>
                    typeof skill === "object" && skill !== null
                      ? "skill_name" in skill
                        ? skill.skill_name
                        : String(skill)
                      : String(skill),
                );

                return (
                  <div
                    key={exp.id || i}
                    className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-primary/40 transition-colors space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-purple-600 shrink-0" />
                          {exp.role}
                        </h3>
                        <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mt-0.5">
                          {exp.company}
                          {exp.location && (
                            <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">
                              • {exp.location}
                            </span>
                          )}
                        </p>
                      </div>

                      {duration && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 px-3 py-1.5 rounded-lg border border-slate-100 dark:border-slate-800 self-start sm:self-center shrink-0">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>{duration}</span>
                        </div>
                      )}
                    </div>

                    {exp.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                        {exp.description}
                      </p>
                    )}

                    {skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {skills.map((skillName, idx) => (
                          <span
                            key={`${skillName}-${idx}`}
                            className="px-2.5 py-0.5 bg-purple-50 dark:bg-purple-950/40 border border-purple-200/50 dark:border-purple-800/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-md"
                          >
                            {skillName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more experience records...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ExperienceTab;
